<script setup lang="ts">
import { ref } from "vue";
import ToolTip from "../../ToolTip.vue";
import { locale } from "../../../locale";
import DeviceAllert from "../../icons/DeviceAllert.vue";
import { cameraDeviceError, desiredMediaState } from "../../../states/media/state";
import { mediaActions } from "../../../states/media/actions";
const btn = ref<HTMLElement | null>(null);
const click = () => {
  if (!cameraDeviceError.value) {
    if(desiredMediaState.cameraEnabled) {
      mediaActions.cameraOff()
    } else {
      mediaActions.cameraOn()
    }
  }
};
</script>
<template>
  <button ref="btn" class="camera-button" @click="click" :class="{
    active: desiredMediaState.cameraEnabled,
    error: !!cameraDeviceError
  }">
    <svg v-if="desiredMediaState.cameraEnabled" width="24" height="24" viewBox="0 0 24 24" fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15.6927 10.8001L20.3401 7.0241C20.4434 6.94029 20.5749 6.88323 20.718 6.86013C20.8612 6.83702 21.0096 6.8489 21.1444 6.89427C21.2793 6.93964 21.3946 7.01646 21.4757 7.11503C21.5569 7.2136 21.6003 7.32951 21.6004 7.4481V16.5521C21.6003 16.6707 21.5569 16.7866 21.4757 16.8852C21.3946 16.9837 21.2793 17.0606 21.1444 17.1059C21.0096 17.1513 20.8612 17.1632 20.718 17.1401C20.5749 17.117 20.4434 17.0599 20.3401 16.9761L15.6927 13.2001M15.6927 13.2001V8.4001C15.6927 7.92271 15.4593 7.46487 15.0438 7.12731C14.6284 6.78974 14.0649 6.6001 13.4773 6.6001L4.33546 6.61456C3.85129 6.66473 3.3981 6.84388 3.04926 7.12731C2.6338 7.46487 2.40039 7.92271 2.40039 8.4001V15.6001C2.40039 16.0775 2.6338 16.5353 3.04926 16.8729C3.46473 17.2105 4.02822 17.4001 4.61578 17.4001H13.4773C14.0649 17.4001 14.6284 17.2105 15.0438 16.8729M15.6927 13.2001L15.6928 15.6001C15.6928 16.0775 15.4593 16.5353 15.0438 16.8729M15.0438 16.8729C15.0859 16.8387 15.1261 16.8033 15.1644 16.7667"
        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
    </svg>
    <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15.6927 10.8L20.3401 7.02405C20.4434 6.94024 20.5749 6.88318 20.718 6.86008C20.8612 6.83697 21.0096 6.84885 21.1444 6.89422C21.2793 6.93959 21.3946 7.01641 21.4757 7.11498C21.5569 7.21356 21.6003 7.32946 21.6004 7.44805V16.552C21.6003 16.6706 21.5569 16.7865 21.4757 16.8851C21.3946 16.9837 21.2793 17.0605 21.1444 17.1059C21.0096 17.1512 20.8612 17.1631 20.718 17.14C20.5749 17.1169 20.4434 17.0599 20.3401 16.976L15.6927 13.2V8.40005C15.6927 7.92266 15.4593 7.46482 15.0438 7.12726C14.6284 6.78969 14.0649 6.60005 13.4773 6.60005H9.00039M14.5764 16.976C14.1609 17.3136 14.0649 17.4 13.4773 17.4H4.61578C4.02822 17.4 3.46473 17.2104 3.04926 16.8728C2.6338 16.5353 2.40039 16.0774 2.40039 15.6V8.40005C2.40039 7.92266 2.6338 7.46482 3.04926 7.12726C3.3981 6.84383 3.72345 6.65745 4.20762 6.60728M2.40039 4.80005L18.0004 20.4"
        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
    </svg>
    <ToolTip :placement="'top'" :reference="btn">{{
      cameraDeviceError ? locale.deviceErrors.common : desiredMediaState.cameraEnabled ? locale.cameraoff : locale.cameraon
    }}</ToolTip>
    <div v-if="cameraDeviceError" class="error-allert">
      <DeviceAllert />
    </div>
  </button>
</template>
<style scoped>
.camera-button {
  width: 44px;
  height: 44px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  background: var(--bottom-btn-off-bg-regular);
  color: var(--bottom-btn-off-content-regular);
  backdrop-filter: blur(2px);
  border-radius: 50px;
  border: none;
  cursor: pointer;
  position: relative;
}

.error-allert {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 16px;
  height: 16px;
}

.camera-button.error {
  cursor: default;
}

.camera-button:hover:not(.error) {
  background-color: var(--bottom-btn-off-bg-hover);
  color: var(--bottom-btn-off-content-hover);
}

.camera-button:active:not(.error) {
  background-color: var(--bottom-btn-off-bg-active);
  color: var(--bottom-btn-off-content-active);
}

.camera-button:focus:not(.error) {
  outline: none;
}

.camera-button.active:hover:not(.error) {
  background-color: var(--bottom-btn-on-bg-hover);
  color: var(--bottom-btn-on-content-hover);
}

.camera-button.active:active:not(.error) {
  background-color: var(--bottom-btn-on-bg-active);
  color: var(--bottom-btn-on-content-active);
}

.camera-button.active:not(.error) {
  background-color: var(--bottom-btn-on-bg-regular);
  color: var(--bottom-btn-on-content-regular);
}
</style>
